import { ReactNode, useEffect, useRef } from 'react';
import { cx } from '@linaria/atomic';
import { isFunction } from 'lodash-es';
import { IconName } from '@frontend/icons';
import { mergeClasses } from '../../../helpers/merge-classes';
import { Button } from '../../button';
import { Text } from '../../text';
import { TextLink } from '../../text-link/text-link.component';
import { useModalContext } from '../provider/modal.provider';
import { withClose, type ClickHandler } from './helpers';
import {
  modalFooter,
  modalFooterHelperText,
  modalFooterLayout,
  modalFooterPrimaryButton,
  modalFooterSecondaryButton,
  modalFooterTertiaryButton,
  modalFooterTertiaryTextLink,
  modalFooterTruncate,
  stickyModalFooter,
} from './modal-actions.styles';

type Props = {
  className?: string;
  showPrimary?: boolean;
  disablePrimary?: boolean;
  disableSecondary?: boolean;
  onBackClick?: ClickHandler;
  onPrimaryClick?: 'submit' | ClickHandler;
  onSecondaryClick?: ClickHandler;
  backLabel?: ReactNode;
  primaryLabel?: string;
  secondaryLabel?: string;
  backTrackingId?: string;
  primaryTrackingId?: string;
  secondaryTrackingId?: string;
  destructive?: boolean;
  helperText?: string;
  primaryLoading?: boolean;
  secondaryLoading?: boolean;
  tertiaryLoading?: boolean;
};

/**
 * @deprecated Use ModalFooter instead
 * Component for adding secondary + primary actions to a modal
 * @param {string} [props.className] Optional css/classname
 * @param {boolean} [props.disablePrimary] Should the primary button be disabled?
 * @param {boolean} [props.disableSecondary] Should the secondary button be disabled?
 * @param {string | function} props.onPrimaryClick Either 'submit' (for buttons controlling a form in a modal) or a click handler function.
 * @param {function} [props.onSecondaryClick] Optionals click handler for the secondary action. Defaults to the modals onClose function.
 * @param {string} [props.primaryLabel] Optional text for the primary button (default = 'Save')
 * @param {string} [props.secondaryLabel] Optional text for the secondary button (default = 'Cancel')
 */
export const ModalActions = ({
  disablePrimary,
  disableSecondary,
  showPrimary = true,
  primaryLabel = 'Save',
  secondaryLabel = 'Cancel',
  backLabel,
  onBackClick,
  onSecondaryClick,
  onPrimaryClick,
  backTrackingId,
  primaryTrackingId,
  secondaryTrackingId,
  destructive,
  helperText,
  primaryLoading,
  secondaryLoading,
  tertiaryLoading,
  className,
}: Props) => {
  const isForm = !isFunction(onPrimaryClick);
  const { setActionStateChanged, onClose } = useModalContext();
  const primaryDisabled = useRef(disablePrimary);
  const secondaryDisabled = useRef(disableSecondary);

  useEffect(() => {
    if (import.meta.env.MODE !== 'production') {
      console.warn('ModalActions is deprecated. Use ModalFooter instead');
    }
  }, []);

  useEffect(() => {
    /**
     * We trigger this side effect to allow consumers to hook into the action state
     */
    setActionStateChanged({
      primary: primaryDisabled.current !== disablePrimary,
      secondary: secondaryDisabled.current !== disableSecondary,
    });

    primaryDisabled.current = disablePrimary;
    secondaryDisabled.current = disableSecondary;
  }, [disablePrimary, disableSecondary]);

  return (
    <ModalFooter
      primary={{
        label: showPrimary ? primaryLabel : '',
        trackingId: primaryTrackingId,
        onClick: isForm ? undefined : withClose(onClose, onPrimaryClick),
        disabled: disablePrimary,
        destructive,
        loading: primaryLoading,
        type: isForm ? 'submit' : 'button',
      }}
      secondary={{
        label: secondaryLabel,
        trackingId: secondaryTrackingId,
        onClick: onSecondaryClick ?? (() => true),
        disabled: disableSecondary,
        loading: secondaryLoading,
      }}
      tertiary={{
        label: backLabel,
        onClick: onBackClick,
        trackingId: backTrackingId,
        loading: tertiaryLoading,
      }}
      helperText={helperText}
      className={className}
    />
  );
};

type ButtonProps = {
  loading?: boolean;
  iconName?: IconName;
  label?: string;
  hoverLabel?: ReactNode;
  trackingId?: string;
  /**
   * Return `true` to close the modal automatically
   */
  onClick?: ClickHandler;
  disabled?: boolean;
  destructive?: boolean;
  autofocus?: boolean;
  showLabelWhenDisabled?: boolean;
};

type ModalFooterProps = {
  primary?: ButtonProps & {
    type?: 'button' | 'submit';
  };
  secondary?: ButtonProps;
  // Allow node as label for backwards compatibility
  tertiary?: Omit<ButtonProps, 'label'> & { label?: ReactNode };
  helperText?: string;
  children?: ReactNode;
  className?: string;
};

export const ModalFooter = ({
  primary = {
    type: 'button',
  },
  secondary = {},
  tertiary = {},
  helperText,
  children,
  className,
}: ModalFooterProps) => {
  const { focusTimeout = 0, show, onClose } = useModalContext();

  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  const secondaryButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (show) {
      setTimeout(() => {
        if (secondary.autofocus) {
          secondaryButtonRef.current?.focus();
        } else if (primary.autofocus) {
          primaryButtonRef.current?.focus();
        }
      }, focusTimeout + 100);
    }
  }, [show, focusTimeout, primary.autofocus, secondary.autofocus]);

  if (children) {
    return <footer className={cx(modalFooter, mergeClasses('wv-modal-footer', className))}>{children}</footer>;
  }

  return (
    <footer className={cx(modalFooter, modalFooterLayout, mergeClasses('wv-modal-footer', className))}>
      {/* This is for backwards compatibility - we should remove after moving modals away from Modal.Actions */}
      {tertiary.label && typeof tertiary.label !== 'string' && (
        <TextLink
          size='large'
          weight='bold'
          className={cx(modalFooterTertiaryTextLink, 'wv-modal-footer-tertiary-button wv-text-link')}
          onClick={withClose(onClose, tertiary.onClick)}
          trackingId={tertiary.trackingId}
        >
          {tertiary.label}
        </TextLink>
      )}
      {tertiary.label && typeof tertiary.label === 'string' && (
        <Button
          size='large'
          loading={tertiary.loading}
          iconName={tertiary.iconName}
          hoverLabel={tertiary.hoverLabel}
          showLabelWhenDisabled={tertiary.showLabelWhenDisabled}
          destructive={tertiary.destructive}
          trackingId={tertiary.trackingId}
          disabled={tertiary.disabled}
          className={cx(modalFooterTertiaryButton, 'wv-modal-footer-tertiary-button wv-button')}
          variant='tertiary'
          onClick={withClose(onClose, tertiary.onClick)}
        >
          <span className={modalFooterTruncate}>{tertiary.label}</span>
        </Button>
      )}

      {helperText && (
        <Text as='span' className={cx(modalFooterHelperText, 'wv-modal-footer-helper-text')} color='subdued'>
          {helperText}
        </Text>
      )}
      {secondary.label && (
        <Button
          size='large'
          loading={secondary.loading}
          iconName={secondary.iconName}
          hoverLabel={secondary.hoverLabel}
          showLabelWhenDisabled={secondary.showLabelWhenDisabled}
          destructive={secondary.destructive}
          trackingId={secondary.trackingId}
          disabled={secondary.disabled}
          className={cx(modalFooterSecondaryButton, 'wv-modal-footer-secondary-button wv-button')}
          variant='secondary'
          ref={secondaryButtonRef}
          /**
           * If there's a label, but no onClick, we assume the secondary button should close the modal
           */
          onClick={withClose(onClose, secondary.onClick ?? (() => true))}
          showFocusOutline={secondary.autofocus}
        >
          <span className={modalFooterTruncate}>{secondary.label}</span>
        </Button>
      )}
      {primary.label && (
        <Button
          size='large'
          loading={primary.loading}
          iconName={primary.iconName}
          hoverLabel={primary.hoverLabel}
          showLabelWhenDisabled={primary.showLabelWhenDisabled}
          destructive={primary.destructive}
          trackingId={primary.trackingId}
          disabled={primary.disabled}
          type={primary.type}
          className={cx(modalFooterPrimaryButton, 'wv-modal-footer-primary-button wv-button')}
          onClick={withClose(onClose, primary.onClick)}
          ref={primaryButtonRef}
          showFocusOutline={primary.autofocus}
        >
          <span className={modalFooterTruncate}>{primary.label}</span>
        </Button>
      )}
    </footer>
  );
};

export const StickyModalFooter = ({ children, ...rest }: ModalFooterProps) => {
  return (
    <ModalFooter {...rest} className={cx(stickyModalFooter, rest.className)}>
      {children}
    </ModalFooter>
  );
};
